import React from "react";
import {
    Badge,
    Box,
    Divider,
    Flex,
    Heading,
    IconButton,
    Drawer, 
    DrawerOverlay, 
    DrawerCloseButton, 
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    DrawerFooter, 
    Text,
    Tooltip,
    useDisclosure,
    useToast,
  } from "@chakra-ui/react";

import { Link } from "react-router-dom";

import { Star, Trash, Delete} from "react-feather";
import { formatDate } from '../utils/format-date'

import { ACTIONS, FAVORITES_TARGET, useFavorites, useFavoritesUpdate } from "../contexts/favorites-context";
import { findPropWithDepth } from '../utils/tools'

import { toastRemoveFavoriteFromDrawer } from './toasts'
import { IconCommonConfirmPrompt } from './alerts'

export default function Favorites({target}) {
  if(!target) throw Error('Unsupported favorites target');

  const { isOpen, onOpen, onClose } = useDisclosure()
  const btnRef = React.useRef()

  const favorites = useFavorites();
  const setFavorites = useFavoritesUpdate();
  const toast = useToast();

  const deleteFavorite = (e, item, target) => {
    e.preventDefault(); 
    setFavorites({
      type: ACTIONS.DELETE, 
      payload: { 
        id: target === FAVORITES_TARGET.LAUNCHES ? item.flight_number : item.id, 
        target: target
      }
    });
    toastRemoveFavoriteFromDrawer(toast, item, target);
  }

  return (
    <>
      <Tooltip label="Favorites" placement="left" >
        <IconButton 
          icon={<Star/>}
          mr={5}
          variant="solid"
          size="lg"
          colorScheme="yellow"
          onClick={onOpen}
        />
      </Tooltip>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        finalFocusRef={btnRef}
        size="md"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
              <Heading size="lg">
              Favorites
              </Heading>
          </DrawerHeader>
          <Divider mb={5}/>
          <DrawerBody>
            <Box>
              <Box>
                <Flex mb={5}justifyContent="space-between"><Heading size="md">{target.charAt(0).toUpperCase() + target.slice(1)} ({Object.keys(favorites[target]).length})</Heading>
                  <IconCommonConfirmPrompt 
                    headerText="Clearing all favorites"
                    bodyText="Are you sure?"
                    confirmText="Clear"
                    label="Clear all"
                    labelPlacement="left"
                    icon={Delete} 
                    onConfirmation={() => {
                        setFavorites({
                          type: ACTIONS.CLEAR, 
                          payload: {target: target}
                        });
                    }}
                  />
                </Flex>
                {Object.values(favorites[target]).map( item => {                    
                  return (
                    <FavoriteItem  
                      my={2}             
                      {...getFavoriteItemProps(item, target)}  
                      item={item}           
                      deleteFavorite={deleteFavorite}
                    />                  
                  )
                })} 
              </Box>              
            </Box>
          </DrawerBody>
          <DrawerFooter>
           <Text>Footer</Text>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  )
}

function FavoriteItem({
  item,
  badgeInfo,
  headerData,
  mainData,
  footerData,
  dataIsDate = false,
  navigateToUrl = '',
  deleteFavorite,
  target,
  ...rest
  }){
    
  return (
    <>
      <Box    
      >
        <Flex 
        {...rest}
        p={2}
        borderWidth="1px"
        rounded="md"
        overflow="hidden"
        position="relative"
        >         
          <Box 
            w="90%" 
            as={Link}
            to={navigateToUrl}
          >
            <Box d="flex" alignItems="baseline">
              {badgeInfo ? (
                <Badge px="2" variant="solid" colorScheme="green">
                  {getBadgeText(target, 'green')}
                </Badge>
              ) : (
                <Badge px="2" variant="solid" colorScheme="red">
                    {getBadgeText(target, 'red')}
                </Badge>
              )}
              <Box
                color="gray.500"
                fontWeight="semibold"
                letterSpacing="wide"
                fontSize="xs"
                textTransform="uppercase"
                ml="2"
              >
                {headerData} 
              </Box>
              
            </Box>
            <Box  
              mt="1"
              fontWeight="semibold"
              as="h4"
              lineHeight="tight"
              isTruncated
            >
              {mainData}
            </Box>    

            <Text fontSize="sm">{dataIsDate ? formatDate(footerData) : footerData} </Text>  

          </Box>
          {/*removed tooltip inside drawer due to errors in the console after closing drawer and scrolling*/}
          {/*<Tooltip label="Remove" placement="left">*/}  
          <Box w="10%" position="relative">   
            <Box as={Trash} 
                position="absolute"
                cursor="pointer"
                right={0}
                w={6}
                h={6}
                color="red.500"
                icon={<Trash/>} 
                p={0}
                m={0}
                onClick={ e => { deleteFavorite(e, item, target) }} 
            />
          </Box>   
          {/*</Tooltip> */}
        </Flex>
      </Box>
    </>
  )
} 

const getBadgeText = (target, status) => {
    switch(target){
      case FAVORITES_TARGET.LAUNCHES:
        return status === 'green' ? 'Successful' : 'Failed'; 
      case FAVORITES_TARGET.PADS:
        return status === 'green' ? 'Active' : 'Retired'; 
      default:
        throw Error('Unsupported Target');
    }
    
}

const getFavoriteItemProps = (item, target) => {

  let key, badgeInfo, headerData, mainData, footerData, dataIsDate, navigateToUrl;
                    
  if(target === FAVORITES_TARGET.LAUNCHES) {

    key = item.flight_number;
    badgeInfo = findPropWithDepth(0, item, ["launch_success"])
    headerData = findPropWithDepth(1, item, ["rocket","rocket_name"])  + ' • ' + findPropWithDepth(1, item, ["launch_site","site_name"]) 
    mainData = findPropWithDepth(0, item, ["mission_name"])
    footerData = findPropWithDepth(0, item, ["launch_date_utc"])
    dataIsDate = true;
    navigateToUrl = `/${FAVORITES_TARGET.LAUNCHES}/${item.flight_number.toString()}`

  } else if (target === FAVORITES_TARGET.PADS) {          

    key = item.site_id;
    badgeInfo = findPropWithDepth(0, item, ["status"]) === "active"
    headerData = findPropWithDepth(0, item, ["attempted_launches"])  + ' ATTEMPTED • ' + findPropWithDepth(0, item, ["successful_launches"]) + ' SUCCEEDED'
    mainData = findPropWithDepth(0, item, ["name"]).toUpperCase()
    footerData = findPropWithDepth(0, item, ["vehicles_launched"]).join(', ');
    navigateToUrl = `/launch-pads/${item.site_id}`
  }

  return {
    key,
    badgeInfo,
    headerData,
    mainData,
    footerData,
    dataIsDate,
    navigateToUrl,
    target
  }

}